import { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { CreditCard, Landmark, ShieldCheck, Clock, UserCheck } from "lucide-react";
import RevealLine from "@/components/RevealLine";
import Reveal from "@/components/Reveal";
import { api, apiError } from "@/lib/api";
import { useLang } from "@/i18n";

const PRODUCTS = ["AIR FORCE ONE LIFE", "AIR FORCE ONE", "AIR FORCE ONE PRO MAX"];

export default function Trial() {
  const { tr } = useLang();
  const [form, setForm] = useState({
    product: PRODUCTS[1],
    name: "",
    email: "",
    phone: "",
    company: "",
    address: "",
    city: "",
    zip_code: "",
    preferred_date: "",
    notes: "",
    payment_method: "stripe",
  });
  const [busy, setBusy] = useState(false);
  const [sentBonifico, setSentBonifico] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/trials", {
        ...form,
        origin_url: window.location.origin,
      });
      if (data.payment_required && data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      setSentBonifico(true);
    } catch (err) {
      toast.error(apiError(err) ?? tr({ it: "Errore nell'invio.", en: "Submission error." }));
    } finally {
      setBusy(false);
    }
  };

  const STEPS = [
    { icon: UserCheck, t: { it: "Compila il form", en: "Fill in the form" }, d: { it: "Scegli il modello e la data preferita per la prova.", en: "Choose the model and your preferred trial date." } },
    { icon: ShieldCheck, t: { it: "Cauzione € 150", en: "€ 150 deposit" }, d: { it: "Paga con carta o bonifico. Restituita o scalata dall'acquisto.", en: "Pay by card or bank transfer. Refunded or deducted from purchase." } },
    { icon: Clock, t: { it: "Un giorno con il tecnico", en: "One day with a technician" }, d: { it: "Un nostro tecnico porta e installa il purificatore a casa tua per un giorno intero.", en: "Our technician brings and installs the purifier at your home for a full day." } },
  ];

  return (
    <div data-testid="page-trial" className="bg-white text-[#0a0a0a] pt-32 pb-24">
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12">
        <div className="font-mono text-[10px] tracking-[0.3em] text-[#007A8A] mb-8">
          {tr({ it: "— PROVA A CASA / 1 GIORNO CON TECNICO", en: "— HOME TRIAL / 1 DAY WITH TECHNICIAN" })}
        </div>
        <h1 className="font-display text-[clamp(3rem,10vw,10rem)] leading-[0.86] tracking-tighter max-w-6xl">
          <div><RevealLine delay={0.2}>{tr({ it: "Provalo", en: "Try it" })}</RevealLine></div>
          <div>
            <RevealLine delay={0.35} className="font-editorial italic font-normal text-[#3f3f46]">
              {tr({ it: "a casa tua.", en: "at your home." })}
            </RevealLine>
          </div>
        </h1>
        <Reveal delay={0.5} className="mt-12 max-w-2xl">
          <p className="font-body text-lg text-[#3f3f46] leading-relaxed">
            {tr({
              it: "Prova un purificatore AIR FORCE ONE per un giorno intero, a casa o in azienda, con l'assistenza di un nostro tecnico. È richiesta una cauzione di € 150, restituita al termine della prova o scalata dall'acquisto.",
              en: "Try an AIR FORCE ONE purifier for a full day, at home or in your company, with the assistance of our technician. A € 150 deposit is required, refunded at the end of the trial or deducted from your purchase.",
            })}
          </p>
        </Reveal>

        {/* Steps */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 border-l border-t border-[#e4e4e7]">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <Reveal key={i} delay={i * 0.05}>
                <div className="border-r border-b border-[#e4e4e7] p-8 bg-[#f4f4f5] h-full">
                  <Icon size={22} className="text-[#007A8A] mb-6" />
                  <div className="font-mono text-[10px] tracking-[0.3em] text-[#52525b] mb-3">0{i + 1}</div>
                  <h3 className="font-display text-2xl tracking-tighter mb-3">{tr(s.t)}</h3>
                  <p className="font-body text-sm text-[#3f3f46] leading-relaxed">{tr(s.d)}</p>
                </div>
              </Reveal>
            );
          })}
        </div>

        {/* Form */}
        <div className="mt-16 max-w-4xl">
          {sentBonifico ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="border border-[#007A8A] p-12 md:p-16 bg-[#f4f4f5]"
              data-testid="trial-sent-bonifico"
            >
              <div className="font-mono text-[10px] tracking-[0.3em] text-[#007A8A] mb-4">● RICEVUTA</div>
              <h3 className="font-display text-4xl md:text-5xl tracking-tighter mb-6">
                {tr({ it: "Richiesta registrata.", en: "Request received." })}
              </h3>
              <p className="font-body text-[#3f3f46] max-w-lg leading-relaxed">
                {tr({
                  it: "Ti invieremo via email le coordinate bancarie per la cauzione di € 150 e ti contatteremo per confermare la data della prova con il nostro tecnico.",
                  en: "We will email you the bank details for the € 150 deposit and contact you to confirm the trial date with our technician.",
                })}
              </p>
            </motion.div>
          ) : (
            <form
              onSubmit={submit}
              data-testid="trial-form"
              className="border border-[#e4e4e7] bg-[#f4f4f5] p-8 md:p-12 space-y-8"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label={tr({ it: "Modello da provare", en: "Model to try" })} required>
                  <select value={form.product} onChange={set("product")} data-testid="trial-product" className="input-aeras">
                    {PRODUCTS.map((p) => (
                      <option key={p} value={p} className="bg-[#f4f4f5]">{p}</option>
                    ))}
                  </select>
                </Field>
                <Field label={tr({ it: "Data preferita", en: "Preferred date" })} required>
                  <input required type="date" value={form.preferred_date} onChange={set("preferred_date")} data-testid="trial-date" className="input-aeras" />
                </Field>
                <Field label={tr({ it: "Nome e cognome", en: "Full name" })} required>
                  <input required value={form.name} onChange={set("name")} data-testid="trial-name" className="input-aeras" />
                </Field>
                <Field label={tr({ it: "Azienda", en: "Company" })}>
                  <input value={form.company} onChange={set("company")} data-testid="trial-company" className="input-aeras" />
                </Field>
                <Field label="Email" required>
                  <input required type="email" value={form.email} onChange={set("email")} data-testid="trial-email" className="input-aeras" />
                </Field>
                <Field label={tr({ it: "Telefono", en: "Phone" })} required>
                  <input required value={form.phone} onChange={set("phone")} data-testid="trial-phone" className="input-aeras" />
                </Field>
                <Field label={tr({ it: "Indirizzo", en: "Address" })} required>
                  <input required value={form.address} onChange={set("address")} data-testid="trial-address" className="input-aeras" />
                </Field>
                <div className="grid grid-cols-2 gap-6">
                  <Field label={tr({ it: "Città", en: "City" })} required>
                    <input required value={form.city} onChange={set("city")} data-testid="trial-city" className="input-aeras" />
                  </Field>
                  <Field label={tr({ it: "CAP", en: "ZIP" })} required>
                    <input required value={form.zip_code} onChange={set("zip_code")} data-testid="trial-zip" className="input-aeras" />
                  </Field>
                </div>
              </div>

              <Field label={tr({ it: "Note (opzionale)", en: "Notes (optional)" })}>
                <textarea rows={3} value={form.notes} onChange={set("notes")} data-testid="trial-notes" className="input-aeras resize-none" />
              </Field>

              {/* Payment method */}
              <div>
                <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#3f3f46] mb-4">
                  {tr({ it: "Cauzione € 150 — metodo di pagamento", en: "€ 150 deposit — payment method" })}
                  <span className="text-[#FF4D00] ml-1">*</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <PayOption
                    active={form.payment_method === "stripe"}
                    onClick={() => setForm({ ...form, payment_method: "stripe" })}
                    icon={CreditCard}
                    testid="trial-pay-stripe"
                    title={tr({ it: "Carta di credito", en: "Credit card" })}
                    desc={tr({ it: "Pagamento sicuro online con Stripe. Conferma immediata.", en: "Secure online payment via Stripe. Instant confirmation." })}
                  />
                  <PayOption
                    active={form.payment_method === "bonifico"}
                    onClick={() => setForm({ ...form, payment_method: "bonifico" })}
                    icon={Landmark}
                    testid="trial-pay-bonifico"
                    title={tr({ it: "Bonifico bancario", en: "Bank transfer" })}
                    desc={tr({ it: "Ti invieremo le coordinate bancarie via email.", en: "We will email you the bank details." })}
                  />
                </div>
              </div>

              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                <p className="font-mono text-[10px] tracking-widest uppercase text-[#52525b] max-w-md">
                  {tr({
                    it: "Cauzione rimborsabile · Dati trattati ai sensi del GDPR 2016/679.",
                    en: "Refundable deposit · Data processed under GDPR 2016/679.",
                  })}
                </p>
                <button type="submit" disabled={busy} data-testid="trial-submit" className="btn-primary disabled:opacity-50">
                  {busy
                    ? tr({ it: "Invio…", en: "Sending…" })
                    : form.payment_method === "stripe"
                      ? tr({ it: "Prosegui al pagamento →", en: "Proceed to payment →" })
                      : tr({ it: "Invia richiesta →", en: "Send request →" })}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>

      <style>{`
        .input-aeras {
          width: 100%; background: transparent; border: none;
          border-bottom: 1px solid #d4d4d8; padding: 0.85rem 0; color: #0a0a0a;
          font-family: 'IBM Plex Sans', sans-serif; font-size: 1rem; outline: none;
          transition: border-color 0.3s ease; color-scheme: light;
        }
        .input-aeras:focus { border-bottom-color: #007A8A; }
      `}</style>
    </div>
  );
}

function PayOption({ active, onClick, icon: Icon, title, desc, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`text-left border p-5 transition-colors ${
        active ? "border-[#007A8A] bg-[#fafafa]" : "border-[#e4e4e7] hover:border-[#3a3d46]"
      }`}
    >
      <div className="flex items-center gap-3 mb-2">
        <Icon size={18} className={active ? "text-[#007A8A]" : "text-[#3f3f46]"} />
        <span className="font-display text-lg tracking-tight">{title}</span>
      </div>
      <p className="font-body text-xs text-[#3f3f46] leading-relaxed">{desc}</p>
    </button>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#3f3f46] mb-2 inline-block">
        {label}
        {required && <span className="text-[#FF4D00] ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}
